import { useState } from 'react';

const OrderConfirmationDialog = ({
  isOpen,
  onClose,
  cart,
  products,
  onConfirm,
  type = 'service', // Add type prop to distinguish between services and products
}) => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    date: type === 'service' ? '' : undefined, // Only for services
    time: type === 'service' ? '' : undefined, // Only for services
  });

  // Get tomorrow's date as minimum date (only for services)
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const minDate = tomorrow.toISOString().split('T')[0];

  // Available time slots (only for services)
  const timeSlots = [
    '09:00',
    '10:00',
    '11:00',
    '12:00',
    '13:00',
    '14:00',
    '15:00',
    '16:00',
    '17:00',
  ];

  if (!isOpen) return null;

  const cartItems = Object.entries(cart)
    .map(([itemId, quantity]) => {
      if (type === 'service') {
        const category = Object.values(products).find(
          (cat) => cat.items && Object.keys(cat.items).includes(itemId),
        );
        const service = category?.items?.[itemId];
        if (!service) return null;

        return {
          ...service,
          quantity,
          total: service.price * quantity,
        };
      } else {
        // Fix for products: search through all categories
        const product = Object.values(products)
          .flat()
          .find((p) => p.id === itemId);
        if (!product) return null;

        return {
          ...product,
          quantity,
          total: product.price * quantity,
        };
      }
    })
    .filter(Boolean);

  const total = cartItems.reduce((sum, item) => sum + item.total, 0);

  const handleSubmit = (e) => {
    e.preventDefault();
    let message;

    if (type === 'service') {
      const formattedDate = new Date(formData.date).toLocaleDateString(
        'en-US',
        {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        },
      );
      message = `Hi, I would like to book these services:\n\n${cartItems
        .map(
          (item) =>
            `${item.name} x${item.quantity} = Rs. ${(
              item.price * item.quantity
            ).toFixed(2)} (${item.duration})`,
        )
        .join('\n')}\n\nTotal: Rs. ${total.toFixed(
        2,
      )}\n\nAppointment Details:\nDate: ${formattedDate}\nTime: ${
        formData.time
      }\n\nCustomer Details:\nName: ${formData.name}\nPhone: ${formData.phone}`;
    } else {
      message = `Hi, I would like to order these products:\n\n${cartItems
        .map(
          (item) =>
            `${item.name} x${item.quantity} = Rs. ${(
              item.price * item.quantity
            ).toFixed(2)}`,
        )
        .join('\n')}\n\nTotal: Rs. ${total.toFixed(
        2,
      )}\n\nDelivery Details:\nName: ${formData.name}\nPhone: ${
        formData.phone
      }`;
    }

    onConfirm({ ...formData, message });
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(formData.message);
      const successEl = document.getElementById('copySuccess');
      successEl.style.opacity = '1';
      setTimeout(() => {
        successEl.style.opacity = '0';
      }, 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
        <h2 className="text-2xl font-bold mb-4">
          {type === 'service' ? 'Confirm Your Booking' : 'Confirm Your Order'}
        </h2>

        {/* Cart Items */}
        <div className="space-y-4 mb-6">
          {cartItems.map((item) => (
            <div
              key={item.id}
              className="flex justify-between items-center border-b pb-2"
            >
              <div>
                <p className="font-medium">{item.name}</p>
                <div className="text-sm text-gray-600">
                  <p>
                    Rs. {item.price.toFixed(2)} × {item.quantity}
                  </p>
                  {type === 'service' && (
                    <p className="text-xs">Duration: {item.duration}</p>
                  )}
                </div>
              </div>
              <div className="text-right">
                <p className="font-medium">
                  Rs. {(item.price * item.quantity).toFixed(2)}
                </p>
                {type === 'service' && (
                  <p className="text-xs text-gray-500">
                    Total duration:{' '}
                    {item.duration.includes('min')
                      ? `${parseInt(item.duration) * item.quantity} min`
                      : `${parseInt(item.duration) * item.quantity} hr`}
                  </p>
                )}
              </div>
            </div>
          ))}
          <div className="flex justify-between items-center pt-2 font-bold">
            <p>Total</p>
            <div className="text-right">
              <p>Rs. {total.toFixed(2)}</p>
              {type === 'service' && (
                <p className="text-xs text-gray-500">
                  {cartItems.reduce(
                    (acc, item) =>
                      acc + parseInt(item.duration) * item.quantity,
                    0,
                  )}{' '}
                  min total
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Common fields */}
          <div>
            <label
              htmlFor="name"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              {type === 'service' ? 'Your Name' : 'Delivery Name'}
            </label>
            <input
              type="text"
              id="name"
              required
              className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              value={formData.name}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, name: e.target.value }))
              }
            />
          </div>

          <div>
            <label
              htmlFor="phone"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Contact Number
            </label>
            <input
              type="tel"
              id="phone"
              required
              className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              value={formData.phone}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, phone: e.target.value }))
              }
            />
          </div>

          {/* Service-specific fields */}
          {type === 'service' && (
            <>
              <div>
                <label
                  htmlFor="date"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Preferred Date
                </label>
                <input
                  type="date"
                  id="date"
                  required
                  min={minDate}
                  className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  value={formData.date}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, date: e.target.value }))
                  }
                />
              </div>

              <div>
                <label
                  htmlFor="time"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Preferred Time
                </label>
                <select
                  id="time"
                  required
                  className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  value={formData.time}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, time: e.target.value }))
                  }
                >
                  <option value="">Select a time</option>
                  {timeSlots.map((time) => (
                    <option key={time} value={time}>
                      {time}
                    </option>
                  ))}
                </select>
              </div>
            </>
          )}

          {/* Action Buttons */}
          <div className="space-y-4">
            <div className="flex space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center justify-center gap-2"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                  />
                </svg>
                Send in WhatsApp
              </button>
            </div>

            {/* Additional Contact Options */}
            <div className="flex justify-center gap-4 pt-2">
              <button
                type="button"
                onClick={handleCopy}
                className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-full transition-colors"
                title="Copy message"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3"
                  />
                </svg>
              </button>

              <a
                href={`sms:${
                  type === 'service' ? '94743633914' : '94776620717'
                }?body=${encodeURIComponent(formData.message)}`}
                className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-full transition-colors"
                title="Send SMS"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                  />
                </svg>
              </a>

              <a
                href={`mailto:?subject=${
                  type === 'service' ? 'Service Booking' : 'Product Order'
                }&body=${encodeURIComponent(formData.message)}`}
                className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-full transition-colors"
                title="Send email"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
              </a>

              <button
                type="button"
                onClick={() => {
                  window.print();
                }}
                className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-full transition-colors"
                title="Print"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"
                  />
                </svg>
              </button>
            </div>

            {/* Success message for copy */}
            <div
              className="text-center text-sm text-gray-500 mt-2"
              style={{ opacity: 0 }}
              id="copySuccess"
            >
              Message copied to clipboard!
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default OrderConfirmationDialog;
